"use client";

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserContext } from '@/app/_module/store/contexts/userContext/userContexts';

/**
 * Hook to invalidate queries when agency context changes
 * This ensures data is refetched with the new agency context
 */
export const useAgencyQueryInvalidation = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();

  // Extract stable primitive value to prevent React #310 infinite loop
  const currentAgencyId = user.currentAgency?.agency?.id;

  useEffect(() => {
    // Only invalidate if agency actually changed
    if (currentAgencyId) {
      // Invalidate specific query prefixes that depend on agency context
      const queryPrefixes = [
        'leads',
        'resources',
        'appointments',
        'reminders',
        'communications',
        'agencies', // Changed from 'agency-dashboard' to match actual query namespace
        'leaderboard',
        'dashboard',
        'premiumSales', // Add premium sales as per project memory
        // STALE-GATE FIX (2026-07-23): agency-status was MISSING here — after
        // switching/selecting an agency every other query refetched but the
        // owner/role status kept the previous context's answer, firing the
        // "Switch to Your Own Agency" gate on an agency the user OWNS until a
        // hard refresh. Prefix match also hits the per-agency keyed entries.
        'agency-status'
      ];

      // Invalidate queries with these prefixes
      queryPrefixes.forEach(prefix => {
        queryClient.invalidateQueries({
          queryKey: [prefix],
          refetchType: 'active'
        });
      });
    }
  }, [currentAgencyId, queryClient]);
};

export default useAgencyQueryInvalidation;
